import React, { useState, useMemo, useEffect } from "react";
import { alpha, Box } from "@mui/system";
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import DownloadIcon from "@mui/icons-material/Download";
import DatagridComponent from "../../../../components/datagridComponent";
import {
  Description,
  HeadphonesOutlined,
  RouterOutlined,
  WifiOutlined,
  CheckCircleOutlineOutlined,
  ReportOutlined,
  CalendarTodayOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import apiCliente from "../../../../services/apiCliente";


// ==========================================
// HELPERS
// ==========================================
function mapStatus(status) {
  const map = {
    AGENDADO: "Agendado",
    EM_EXECUCAO: "Em execução",
    CONCLUIDO: "Concluído",
    FALHA: "Falha",
  };
  return map[status] || status;
}

function formatarData(data) {
  if (!data) return "—";
  return new Date(data).toLocaleDateString("pt-BR");
}

export default function RelatoriosDisponiveisParaDownload() {
  const theme = useTheme();

  // ==========================================
  // ESTADOS
  // ==========================================
  const [tabValue, setTabValue] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGerar, setLoadingGerar] = useState(null);

  // ==========================================
  // BUSCA OS RELATÓRIOS PUBLICADOS
  // ==========================================
  useEffect(() => {
    fetchRelatorios();
  }, []);

  const fetchRelatorios = async () => {
    try {
      setLoading(true);
      const response = await apiCliente.get("/cliente/relatorios/publicados");

      const mapped = (response?.data || []).map((rel) => ({
        id: rel.id,
        nome: rel.nome,
        tipo: rel.tipos?.map((t) => t.tipo).join(", ") || "—",
        frequencia: rel.agendamento?.frequencia || "Único",
        status: mapStatus(rel.status),
        data: formatarData(rel.createdAt),
        dataInicio: formatarData(rel.dataInicial),
        dataFim: formatarData(rel.dataFinal),
        _raw: rel,
      }));

      setRows(mapped);
    } catch (error) {
      console.error("Erro ao buscar relatórios publicados:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // DOWNLOAD DO PDF
  // ==========================================
  const handleBaixar = async (row) => {
    try {
      setLoadingGerar(row.id);
      const response = await apiCliente.get(
        `/cliente/relatorios/download/${row.id}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio-${row.nome}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Erro ao baixar relatório:", error);
    } finally {
      setLoadingGerar(null);
    }
  };

  // ==========================================
  // COLUNAS
  // ==========================================
  const getStatusConfig = (status) => {
    switch (status) {
      case "Concluído":
        return {
          color: "success",
          bgColor: theme.palette.chip?.background?.success,
          icon: <CheckCircleOutlineOutlined fontSize="small" />,
        };
      case "Em execução":
        return {
          color: "info",
          bgColor: theme.palette.chip?.background?.medium,
          icon: <CircularProgress size={10} color="inherit" />,
        };
      case "Falha":
        return {
          color: "error",
          bgColor: theme.palette.chip?.background?.critical,
          icon: <ReportOutlined fontSize="small" />,
        };
      case "Agendado":
        return {
          color: "warning",
          bgColor: theme.palette.chip?.background?.high,
          icon: <CalendarTodayOutlined sx={{ fontSize: "14px !important" }} />,
        };
      default:
        return { color: "default", bgColor: "transparent", icon: undefined };
    }
  };

  const getIconByType = (type) => {
    switch (type?.toLowerCase()) {
      case "cpe":
        return <RouterOutlined fontSize="small" />;
      case "onu":
        return <WifiOutlined fontSize="small" />;
      case "atendimento":
        return <HeadphonesOutlined fontSize="small" />;
      default:
        return undefined;
    }
  };

  const columns = [
    {
      field: "nome",
      headerName: "Relatório",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} height="100%">
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: "10px",
              width: "35px",
              height: "35px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Description
              fontSize="small"
              sx={{
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
              }}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.value}
            </Typography>
            {params.row._raw?.agendamento && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.75rem" }}
              >
                Próximo: {formatarData(params.row._raw.agendamento.dataInicial)}
              </Typography>
            )}
            {params.row._raw?.relatorioArquivo?.tamanho && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.75rem", fontWeight: 500 }}
              >
                PDF - {formatarTamanhoArquivo(params.row._raw.relatorioArquivo.tamanho)}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          icon={getIconByType(params.value)}
          sx={{ minWidth: "80px" }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const config = getStatusConfig(params.value);
        return (
          <Chip
            variant="outlined"
            label={params.value}
            color={config.color}
            icon={config.icon}
            size="small"
            sx={{
              bgcolor: config.bgColor,
              "& .MuiChip-icon": { mt: "-1px", ml: "4px" },
            }}
          />
        );
      },
    },
    { field: "data", headerName: "Data de Geração", width: 130 },
    { field: "dataInicio", headerName: "Data de Início", width: 130 },
    { field: "dataFim", headerName: "Data Final", width: 130 },
    {
      field: "acoes",
      headerName: "",
      width: 120,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const isConcluido = params.row.status === "Concluído";
        const isGerando = loadingGerar === params.row.id;

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            height="100%"
            width="100%"
          >
            {isConcluido && (
              <Tooltip title="Baixar PDF">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    isGerando ? (
                      <CircularProgress size={12} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={isGerando}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBaixar(params.row);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "10px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                  }}
                >
                  {isGerando ? "Baixando..." : "Baixar"}
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  // ==========================================
  // FILTRAGEM
  // ==========================================
  const totalConcluido = rows.filter((r) => r.status === "Concluído").length;
  const totalAgendado = rows.filter((r) => r.status === "Agendado").length;

  const filteredRows = useMemo(() => {
    let current = rows;

    if (tabValue === 1)
      current = current.filter((r) => r.status === "Concluído");
    if (tabValue === 2)
      current = current.filter((r) => r.status === "Agendado");

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      current = current.filter((row) =>
        Object.values(row).some((v) =>
          String(v).toLowerCase().includes(lower),
        ),
      );
    }

    return current;
  }, [tabValue, searchText, rows]);

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 1,
        bgcolor: theme.palette.background.paper,
        borderRadius: "12px",
      }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          p: 1,
          pb: 0,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Relatórios Disponíveis
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: theme.palette.background.wallpaper,
          p: 2,
          borderRadius: "12px",
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box
              sx={{
                bgcolor: theme.palette.background.paper,
                p: 2,
                borderRadius: "12px",
              }}
            >
              {/* Tabs + Busca */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: 1,
                  borderColor: "divider",
                  mb: 2,
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={(_, v) => setTabValue(v)}
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTabs-indicator": {
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "5px 5px 0 0",
                    },
                    "& button": { textTransform: "none" },
                    width: "100%",
                    "& .MuiTabs-flexContainer": { gap: 0.5 },
                  }}
                >
                  <Tab
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography>Todos</Typography>
                        <Chip
                          label={rows.length}
                          size="small"
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.2,
                            ),
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.primary.light
                                : theme.palette.primary.dark,
                            fontWeight: "bold",
                            height: "16px",
                            minWidth: "16px",
                          }}
                        />
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography>Concluídos</Typography>
                        <Chip
                          label={totalConcluido}
                          size="small"
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.2,
                            ),
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.primary.light
                                : theme.palette.primary.dark,
                            fontWeight: "bold",
                            height: "16px",
                            minWidth: "16px",
                          }}
                        />
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography>Agendados</Typography>
                        <Chip
                          label={totalAgendado}
                          size="small"
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.2,
                            ),
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.primary.light
                                : theme.palette.primary.dark,
                            fontWeight: "bold",
                            height: "16px",
                            minWidth: "16px",
                          }}
                        />
                      </Box>
                    }
                  />
                </Tabs>

                <TextField
                  placeholder="Buscar relatório..."
                  variant="outlined"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "250px",
                    "& .MuiOutlinedInput-root": {
                      height: "36px",
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8.5px 14px",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Box>

              {/* Tabela */}
              <Box sx={{ height: 450, width: "100%" }}>
                <DatagridComponent
                  rows={filteredRows}
                  columns={columns}
                  loading={loading}
                  sxDatagrid={{
                    "& .MuiDataGrid-row": {
                      minHeight: "55px !important",
                      maxHeight: "55px !important",
                    },
                    "& .MuiDataGrid-cell": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

function formatarTamanhoArquivo(bytes) {
  if (!bytes || isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}
